import { FormFieldsGenerator, TFormFieldsGeneratorProps } from 'src/components/FormFieldsGenerator';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TAllFieldNames,
  TFieldsUnionWithValue,
  TValuesByFieldName,
} from 'src/utils/assetsFields/assetsFields.types';
import { configType, isLibrary } from 'src/mocks/configurationSettings';
import {
  getAllFieldTemplatesWithValuesForAsset,
  getAssetValues,
} from 'src/utils/assetsFields/fieldTemplatesWithValues';
import {
  selectAssets,
  selectConfigurationCharacteristic,
  selectReadOnly,
  selectSettingsData,
  selectUsedAssetsNames,
} from 'src/redux/configuration/configuration.selectors';

import { EFormVariant } from 'src/typings/base-types';
import { GRID_MARKET_MODE } from 'src/constants/application';
import { TFormAssetsParamsProps } from './FormAssetsParams.types';
import { updateFields } from 'src/utils/assetsFields/updateFields';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import { useSelector } from 'react-redux';
import { validateFields } from 'src/utils/validation';
import { validatorsByAssetType } from 'src/utils/assetsFields/fieldValidators';

export const FormAssetsParams: React.FC<TFormAssetsParamsProps> = ({
  hasErrorsRef,
  formVariant,
  assetUuid,
  assetType,
  currentValues,
  onSubmit,
  ...formFieldsGeneratorProps
}) => {
  const [fields, setFields] = useState<TFormFieldsGeneratorProps['fields'] | null>(null);
  const [errors, setErrors] = useState<TFormFieldsGeneratorProps['errors']>(null);
  const usedAssetsNames = useSelector(selectUsedAssetsNames);
  const assets = useSelector(selectAssets);
  const resolvedAssetType = assetType || assets[assetUuid].type;
  const settingsData = useSelector(selectSettingsData);
  const configurationCharacteristic = useSelector(selectConfigurationCharacteristic);
  const newAssetType = useRef(assetType);
  const readOnly = useSelector(selectReadOnly);
  const currentRefs = useCurrentRefs({
    configurationCharacteristic,
    currentValues,
    settingsData,
    usedAssetsNames,
  });

  function handleValidation(fields: TFieldsUnionWithValue[]) {
    return validateFields({
      validators: validatorsByAssetType[resolvedAssetType]({
        settingsData,
        fields,
        isLibrary,
        usedAssetsNames,
        currentValues,
        configurationCharacteristic,
      }),
      fields,
    });
  }

  const handleChange: TFormFieldsGeneratorProps['onChange'] = ({ name, value }) => {
    if (!fields || !assetUuid || !resolvedAssetType) return;

    const payload = {
      settingsData,
      isLibrary,
      configType,
      configurationCharacteristic,
    };
    let newFields;

    if (name === GRID_MARKET_MODE) {
      // This is special case where we completely switch a device
      const _value = value as TValuesByFieldName['GRID_MARKET_MODE'];
      newAssetType.current = _value === 'MarketMakerMode' ? 'MarketMaker' : 'InfiniteBus';

      newFields = getAllFieldTemplatesWithValuesForAsset({
        type: newAssetType.current,
        values: { ...getAssetValues(fields), [name]: _value },
        ...payload,
      });
    } else {
      newFields = updateFields({
        type: resolvedAssetType,
        fields,
        updatedField: { name: name as TAllFieldNames, value },
        ...payload,
      });
    }

    const { errors } = handleValidation(newFields);

    setFields(newFields);
    setErrors(errors);
  };

  const handleBlur: TFormFieldsGeneratorProps['onBlur'] = () => {
    if (!fields) return;
    const { errors } = handleValidation(fields);
    setErrors(errors);
  };

  const handleSubmit: TFormFieldsGeneratorProps['onSubmit'] = () => {
    if (!fields) return;

    const { errors } = handleValidation(fields);

    if (errors) {
      setErrors(errors);
    } else {
      onSubmit({ assetUuid, values: getAssetValues(fields), assetType: newAssetType.current });
    }
  };

  useEffect(
    function setUnfilteredFields() {
      const { configurationCharacteristic, currentValues, settingsData } = currentRefs.current;

      setFields(
        getAllFieldTemplatesWithValuesForAsset({
          type: resolvedAssetType,
          settingsData,
          values: currentValues,
          isLibrary,
          configType,
          configurationCharacteristic,
        }),
      );
    },
    [resolvedAssetType, currentRefs],
  );

  const renderedFields = useMemo(() => {
    switch (formVariant) {
      case EFormVariant.Advanced:
        return fields?.filter(
          (f) => f.formView === EFormVariant.Advanced || f.formView === EFormVariant.Express,
        );

      default:
        return fields?.filter((f) => f.formView === formVariant);
    }
  }, [fields, formVariant]);

  useEffect(() => {
    if (hasErrorsRef) {
      hasErrorsRef.current = Boolean(errors);
    }
  }, [hasErrorsRef, errors]);

  if (!renderedFields) return null;
  return (
    <FormFieldsGenerator
      fields={renderedFields}
      onChange={handleChange}
      onBlur={handleBlur}
      errors={errors}
      onSubmit={handleSubmit}
      focusField="name"
      readOnly={readOnly}
      {...formFieldsGeneratorProps}
    />
  );
};
